<template>
  <div class="relative w-full h-[700px]">
    <!-- Map Container -->
    <div id="map" class="absolute inset-0 w-full h-full rounded-lg"></div>

    <div
      v-show="showPanel"
      class="absolute top-3 left-3 w-96 max-h-[calc(100%-20px)] bg-base-100 rounded-lg shadow-lg overflow-y-auto z-10"
    >
      <div class="p-4 border-b m-3">
        <h3 class="text-lg font-semibold">Objekto informacija</h3>
      </div>
      <div v-if="selectedFeature" class="overflow-x-auto m-3">
        <table class="table table-zebra">
          <tbody>
            <tr v-for="(value, key) in selectedFeature" :key="key">
              <th class="font-medium whitespace-nowrap">{{ key }}</th>
              <td class="text-gray-700">{{ value }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, defineProps } from "vue";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

const props = defineProps({
  styleUrl: {
    type: String,
    required: true,
  },
  center: {
    type: Array,
    default: () => [23.53, 55.8],
  },
  zoom: {
    type: Number,
    default: 13,
  },
});

let map = null;
const showPanel = ref(false);
const selectedFeature = ref(null);

const showInfoPanel = (properties) => {
  selectedFeature.value = properties;
  showPanel.value = true;
};

const initializeMap = () => {
  map = new maplibregl.Map({
    hash: true,
    container: "map",
    style: props.styleUrl,
    center: props.center,
    zoom: props.zoom,
  });

  // Add other layers
  map.on("load", () => {
    map.addSource("osm", {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    });

    // Add OSM layer at the bottom of the layers stack
    map.addLayer(
      {
        id: "osm-tiles",
        type: "raster",
        source: "osm",
        minzoom: 0,
        maxzoom: 22,
      },
      map.getStyle().layers[0].id,
    );
  });

  // Add navigation control
  map.addControl(new maplibregl.NavigationControl());

  // Add geolocate control
  const geolocate = new maplibregl.GeolocateControl({
    positionOptions: {
      enableHighAccuracy: true,
    },
    trackUserLocation: true,
  });
  map.addControl(geolocate);

  map.on("style.load", () => {
    const sources = ["tedr_sil_t", "tedr_sil_l", "tedr_sil_tinkl"];

    // Get all layers from these sources
    const style = map.getStyle();
    const layersFromSources = style.layers.filter((layer) =>
      sources.includes(layer.source),
    );

    layersFromSources.forEach((layer) => {
      // Click handler
      map.on("click", layer.id, (e) => {
        if (e.features.length > 0) {
          showInfoPanel(e.features[0].properties);
        }
      });

      // Hover effects
      map.on("mouseenter", layer.id, () => {
        map.getCanvas().style.cursor = "pointer";
      });

      map.on("mouseleave", layer.id, () => {
        map.getCanvas().style.cursor = "";
      });
    });
  });

  // Close panel when clicking outside
  map.on("click", (e) => {
    const features = map.queryRenderedFeatures(e.point, {
      sources: ["tedr_sil_t", "tedr_sil_l", "tedr_sil_tinkl"],
    });

    if (features.length === 0) {
      showPanel.value = false;
      selectedFeature.value = null;
    }
  });
};

onMounted(() => {
  initializeMap();
});

onUnmounted(() => {
  if (map) {
    map.remove();
  }
});
</script>
